
function escapeRegex(str) {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function escapeRegexChar(c) {
    return /[.+*?^${}()|[\]\\]/.test(c) ? '\\' + c : c;
}


export function jumpToMatchIndex(editor, pattern, targetIndex, options = {}) {
    if (!editor || !pattern || targetIndex < 1) return { found: false };
    const { mode = 'wildcard', caseSensitive = false, wholeWord = false } = options;

    try {
        const text = editor.getValue();
        const re = buildRegex(pattern, mode, caseSensitive, wholeWord, true);
        re.lastIndex = 0;

        // Collect all matches
        const matches = [];
        let m;
        while ((m = re.exec(text)) !== null) {
            matches.push({ start: m.index, end: m.index + m[0].length });
            if (m[0].length === 0) re.lastIndex++;
        }

        if (matches.length === 0) return { found: false, total: 0 };

        // Clamp to valid range
        const idx = Math.max(1, Math.min(targetIndex, matches.length)) - 1;
        const match = matches[idx];

        // Convert flat offset → row/col
        const lines = text.split('\n');
        let remaining = match.start;
        let row = 0;
        while (row < lines.length && remaining > lines[row].length) {
            remaining -= lines[row].length + 1;
            row++;
        }

        const Range = window.ace?.require('ace/range')?.Range;
        if (!Range) return { found: false };

        // Compute end position
        let eRemaining = match.end;
        let eRow = 0;
        while (eRow < lines.length && eRemaining > lines[eRow].length) {
            eRemaining -= lines[eRow].length + 1;
            eRow++;
        }

        const range = new Range(row, remaining, eRow, eRemaining);
        editor.selection.setRange(range);
        editor.scrollToLine(row, true, true);

        return { found: true, current: idx + 1, total: matches.length };
    } catch {
        return { found: false };
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// Returns the 1-based index of whichever match the cursor is currently inside
// Returns null if nothing can be determined
// ─────────────────────────────────────────────────────────────────────────────
export function getCurrentMatchIndex(editor, pattern, options = {}) {
    if (!editor || !pattern) return null;
    const { mode = 'wildcard', caseSensitive = false, wholeWord = false } = options;

    try {
        const text = editor.getValue();
        const re = buildRegex(pattern, mode, caseSensitive, wholeWord, true);
        re.lastIndex = 0;

        // Collect all match start/end offsets in the flat string
        const matches = [];
        let m;
        while ((m = re.exec(text)) !== null) {
            matches.push({ start: m.index, end: m.index + m[0].length });
            if (m[0].length === 0) re.lastIndex++; // avoid infinite loop on zero-width
        }

        if (matches.length === 0) return null;

        // Convert cursor {row,col} → flat string offset
        const cursor = editor.getCursorPosition();
        const lines = text.split('\n');
        let offset = 0;
        for (let r = 0; r < cursor.row; r++) {
            offset += lines[r].length + 1; // +1 for the \n
        }
        offset += cursor.column;

        // Find which match the cursor is inside (or just after)
        // Ace places cursor at END of selection after find()
        for (let idx = 0; idx < matches.length; idx++) {
            const { start, end } = matches[idx];
            if (offset >= start && offset <= end) {
                return { current: idx + 1, total: matches.length };
            }
        }

        // Cursor not inside any match — find the nearest upcoming match
        for (let idx = 0; idx < matches.length; idx++) {
            if (matches[idx].start >= offset) {
                return { current: idx + 1, total: matches.length };
            }
        }

        // Cursor is past all matches (wrapped) — show last
        return { current: matches.length, total: matches.length };
    } catch {
        return null;
    }
}
function wildcardToRegexStr(pattern) {
    let i = 0;
    let result = '';

    while (i < pattern.length) {
        const ch = pattern[i];

        // ── ^x special sequences ──────────────────────────────────────────────
        if (ch === '^' && i + 1 < pattern.length) {
            const next = pattern[i + 1];
            i += 2;
            switch (next) {
                case '#': result += '[0-9]'; break;
                case '$': result += '[A-Za-z]'; break;
                case '=': result += '\u2013'; break; // en-dash
                case '+': result += '\u2014'; break; // em-dash
                case 'p': result += '\n'; break;
                case 't': result += '\t'; break;
                case 'l': result += '\v'; break;
                case 'n': result += '\x0E'; break;
                case 'm': result += '\x0C'; break;
                case 'w': result += '[ \t]'; break;
                case 's': result += '\u00A0'; break;
                case '~': result += '\u2011'; break;
                default:
                    result += escapeRegexChar('^') + escapeRegexChar(next);
            }
            continue;
        }

        // ── Word boundary anchors ─────────────────────────────────────────────
        if (ch === '<') { result += '\\b(?=\\w)'; i++; continue; }
        if (ch === '>') { result += '(?<=\\w)\\b'; i++; continue; }

        // ── Wildcards ─────────────────────────────────────────────────────────
        if (ch === '?') { result += '.'; i++; continue; } // any single char
        if (ch === '*') {
            const prev = i > 0 ? pattern[i - 1] : '';
            const next = i + 1 < pattern.length ? pattern[i + 1] : '';

            // Delimiter pairs that trigger "match-everything-inside" mode
            const delimPairs = { '{': '}', '(': ')' };

            if (delimPairs[prev] && delimPairs[prev] === next) {
                // {*} or (*) → match ANY content (incl. spaces) up to closing delimiter
                result += `[^${escapeRegexChar(delimPairs[prev])}]*`;
            } else {
                // standalone com* → match non-whitespace word token only
                result += '\\w*';
            }
            i++;
            continue;
        }

        // ── Character classes [...] with ! negation ───────────────────────────
        if (ch === '[') {
            let cls = '[';
            i++;
            if (i < pattern.length && pattern[i] === '!') {
                cls += '^'; // Word's [!abc] → regex [^abc]
                i++;
            }
            while (i < pattern.length && pattern[i] !== ']') {
                cls += pattern[i++];
            }
            cls += ']';
            if (i < pattern.length && pattern[i] === ']') i++;
            result += cls;
            continue;
        }

        // ── Quantifiers {n}, {n,}, {n,m} — numeric content only ──────────────
        if (ch === '{') {
            const end = pattern.indexOf('}', i);
            if (end !== -1) {
                const inside = pattern.slice(i + 1, end);
                if (/^\d+(,\d*)?$/.test(inside)) {
                    // Valid quantifier: {3}, {2,}, {1,4}
                    result += pattern.slice(i, end + 1);
                    i = end + 1;
                    continue;
                }
                // Non-numeric inside {} → treat { as literal, keep processing
            }
            result += '\\{';
            i++;
            continue;
        }

        // ── @ → one-or-more ───────────────────────────────────────────────────
        if (ch === '@') { result += '+'; i++; continue; }

        // ── Capturing groups ──────────────────────────────────────────────────
        if (ch === '(') { result += '('; i++; continue; }
        if (ch === ')') { result += ')'; i++; continue; }

        // ── Logical OR ────────────────────────────────────────────────────────
        if (ch === '|') { result += '|'; i++; continue; }

        // ── Backslash: escape wildcard specials OR match literal \ ────────────
        if (ch === '\\' && i + 1 < pattern.length) {
            const nextChar = pattern[i + 1];
            const wildcardSpecials = '*?[]{}()@<>^|\\';
            if (wildcardSpecials.includes(nextChar)) {
                // \* → literal *,  \[ → literal [,  \\ → literal \
                result += escapeRegexChar(nextChar);
            } else {
                // \c → matches real backslash+c in text  (\cite → \\cite)
                result += '\\\\' + escapeRegexChar(nextChar);
            }
            i += 2;
            continue;
        }

        // ── Default: escape regex metacharacters ──────────────────────────────
        result += escapeRegexChar(ch);
        i++;
    }

    return result;
}

// ─────────────────────────────────────────────────────────────────────────────
// Build RegExp from pattern + mode
// Always creates a FRESH object (never reuse — stale lastIndex breaks replaceAll)
// ─────────────────────────────────────────────────────────────────────────────

export function buildRegex(pattern, mode, caseSensitive, wholeWord, global = true) {
    if (!pattern) return null;

    let source;
    if (mode === 'regex') source = pattern;
    else if (mode === 'wildcard') source = wildcardToRegexStr(pattern);
    else source = escapeRegex(pattern); // plain text

    if (wholeWord) source = `\\b${source}\\b`;

    const flags = (global ? 'g' : '') + (caseSensitive ? '' : 'i');
    return new RegExp(source, flags || '');
}

// ─────────────────────────────────────────────────────────────────────────────
// Validate pattern
// ─────────────────────────────────────────────────────────────────────────────

export function validatePattern(pattern, mode) {
    if (!pattern) return { valid: true };
    try {
        buildRegex(pattern, mode, false, false, false);
        return { valid: true };
    } catch (e) {
        return { valid: false, error: e.message };
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// Count matches
// ─────────────────────────────────────────────────────────────────────────────

export function countMatches(editor, pattern, options = {}) {
    if (!editor || !pattern) return 0;
    const { mode = 'wildcard', caseSensitive = false, wholeWord = false } = options;
    try {
        // Fresh regex every time — lastIndex always 0
        const re = buildRegex(pattern, mode, caseSensitive, wholeWord, true);
        re.lastIndex = 0;
        return (editor.getValue().match(re) || []).length;
    } catch {
        return 0;
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// Find next / previous  (uses Ace's built-in cursor movement)
// ─────────────────────────────────────────────────────────────────────────────

export function wildcardFind(editor, pattern, options = {}) {
    if (!editor || !pattern) return { found: false, message: 'No pattern' };
    const {
        mode = 'wildcard',
        caseSensitive = false,
        wholeWord = false,
        backwards = false,
        wrap = true,
    } = options;

    try {
        const isPlain = mode === 'plain';

        // For Ace's find(), we always pass the regex SOURCE string + regExp:true
        // (except plain mode which uses literal string matching)
        const needle = isPlain
            ? pattern
            : buildRegex(pattern, mode, caseSensitive, wholeWord, false).source;
        //                                                         ↑ non-global for find()

        const result = editor.find(needle, {
            backwards,
            wrap,
            caseSensitive,
            wholeWord: isPlain && wholeWord,
            regExp: !isPlain,
            skipCurrent: true,
        });

        if (result) {
            editor.scrollToLine(editor.getCursorPosition().row, true, true);
            return { found: true, message: 'Match found' };
        }
        return { found: false, message: 'No matches found' };
    } catch (err) {
        return { found: false, message: `Invalid pattern: ${err.message}` };
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// Replace current match then advance to next
// ─────────────────────────────────────────────────────────────────────────────
function applyBackReferences(replacement, matchArray) {
    // Replace $1, $2 … with corresponding capture groups
    // Replace $& with the full match (matchArray[0])
    return replacement
        .replace(/\$&/g, matchArray[0] ?? '')
        .replace(/\$(\d+)/g, (_, n) => matchArray[parseInt(n)] ?? '');
}

// ─────────────────────────────────────────────────────────────────────────────
// Internal: does the current editor selection exactly match the pattern?
// ─────────────────────────────────────────────────────────────────────────────
function currentSelectionMatches(editor, pattern, mode, caseSensitive, wholeWord) {
    try {
        const range = editor.selection.getRange();
        if (!range || range.isEmpty()) return false;

        const selectedText = editor.session.getTextRange(range);
        if (!selectedText) return false;

        const re = buildRegex(pattern, mode, caseSensitive, wholeWord, false);
        re.lastIndex = 0;

        // Must match the ENTIRE selected text, not just a substring
        const m = re.exec(selectedText);
        return m !== null && m[0] === selectedText;
    } catch {
        return false;
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// Replace current match then advance to next
// ─────────────────────────────────────────────────────────────────────────────
export function wildcardReplace(editor, pattern, replacement, options = {}) {
    if (!editor || !pattern) return { replaced: false, message: 'No pattern' };
    const {
        mode = 'wildcard',
        caseSensitive = false,
        wholeWord = false,
        wrap = true,
    } = options;

    try {
        const isPlain = mode === 'plain';
        const needle = isPlain
            ? pattern
            : buildRegex(pattern, mode, caseSensitive, wholeWord, false).source;

        // ── Step 1: Check if current selection is already the match ──────────
        // If user clicked Find Next first, the match is already highlighted.
        // Replace it directly WITHOUT calling find() (which would skip to next)
        const alreadySelected = currentSelectionMatches(
            editor, pattern, mode, caseSensitive, wholeWord
        );

        if (!alreadySelected) {
            // Nothing valid selected yet — find the first/next match first
            const found = editor.find(needle, {
                wrap,
                caseSensitive,
                wholeWord: isPlain && wholeWord,
                regExp: !isPlain,
                skipCurrent: false,  // ← don't skip, select the nearest match
            });
            if (!found) return { replaced: false, message: 'No matches found' };
        }

        // ── Step 2: Get the currently selected range ─────────────────────────
        const range = editor.selection.getRange();
        if (!range || range.isEmpty()) return { replaced: false, message: 'No selection to replace' };

        // ── Step 3: Resolve back-references in replacement string ────────────
        let finalReplacement = replacement;
        if (!isPlain) {
            const matchedText = editor.session.getTextRange(range);
            const re = buildRegex(pattern, mode, caseSensitive, wholeWord, false);
            re.lastIndex = 0;
            const m = re.exec(matchedText);
            if (m) finalReplacement = applyBackReferences(replacement, m);
        }

        // ── Step 4: Write literally (bypasses Ace's backslash interpretation) ─
        editor.session.replace(range, finalReplacement);

        // ── Step 5: Advance to next match so user sees what's coming ─────────
        editor.find(needle, {
            wrap,
            caseSensitive,
            wholeWord: isPlain && wholeWord,
            regExp: !isPlain,
            skipCurrent: false,
        });

        return { replaced: true, message: 'Replaced 1 occurrence' };
    } catch (err) {
        return { replaced: false, message: `Replace failed: ${err.message}` };
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// Replace ALL — directly rewrites editor text (bypasses Ace find/replaceAll
// which is unreliable for multi-match-per-line wildcard patterns)
// ─────────────────────────────────────────────────────────────────────────────

export function wildcardReplaceAll(editor, pattern, replacement, options = {}) {
    if (!editor || !pattern) return { count: 0, message: 'No pattern' };
    const { mode = 'wildcard', caseSensitive = false, wholeWord = false } = options;

    try {
        // Always fresh regex with lastIndex = 0
        const re = buildRegex(pattern, mode, caseSensitive, wholeWord, true);
        re.lastIndex = 0;

        const original = editor.getValue();
        let count = 0;

        // Use a function replacer — JS never interprets $ in a function return value
        const final = original.replace(re, (...args) => {
            count++;
            // args = [fullMatch, g1, g2, ..., offset, originalStr]
            // We pass args as the "match array" for back-reference resolution
            const matchArray = args.slice(0, args.length - 2); // strip offset + original
            return applyBackReferences(replacement, matchArray);
        });

        if (count === 0) return { count: 0, message: 'No matches found' };

        // Preserve cursor position as best we can
        const cursorPos = editor.getCursorPosition();
        editor.setValue(final, -1);
        editor.moveCursorToPosition(cursorPos);
        editor.clearSelection();

        return { count, message: `Replaced ${count} occurrence${count !== 1 ? 's' : ''}` };
    } catch (err) {
        return { count: 0, message: `Replace all failed: ${err.message}` };
    }
}